import React, { ReactNode } from 'react';
import ModalManager from "../layout/ModalManager";
import Dialog from "../layout/Dialog";
import Panel from "../layout/Panel";
import { LayoutOrientations } from "../../globals";
import { ErrorIcon, AlertIcon, WarningIcon } from "../Iconset";
import Spacer from "../layout/Spacer";

export interface IMessageInfo
{
	title?: string;
	onCloseCallback?: () => void;
	actions?: { label: string, onClick?: () => void }[];
	content: ReactNode;
	icon?: JSX.Element;
	onModalCloseCallback?: () => void;
}

export interface IErrorMessageInfo extends IMessageInfo
{
	code?: string | number;
}

const defaultError: IErrorMessageInfo = {
	title: "Error!",
	onCloseCallback: () => { },
	actions: [{ label: "Confirm" }],
	content: "An error has occured!",
	code: "00000",
	icon: <ErrorIcon scale={1.2} />,
};

const defaultWarning: IMessageInfo = {
	title: "Attention!",
	onCloseCallback: () => { },
	actions: [{ label: "Confirm" }],
	content: "You have been warned!",
	icon: <WarningIcon scale={1.2} />,
}

const DefaultMessage: IMessageInfo = {
	title: "Message",
	onCloseCallback: () => { },
	actions: [{ label: "Confirm" }],
	content: "Message Content",
	icon: <AlertIcon scale={1.2} />,
}

export default class MessagePrompter
{
	static showError(errorData: IErrorMessageInfo)
	{
		//console.log(errorData);
		const error = { ...defaultError, ...errorData };
		ModalManager.addModal({
			allowClickOut: false, // do not allow the user to ignore an error by clicking outside the dialog, they must choose an action to close it
			content:
				<Dialog
				  className={"errorDialog"}
				  data-name={"error-dialog-root-" + error.title}
				  title={
				    <Panel orientation={LayoutOrientations.Horizontal}
				      data-name={"error-dialog-header-" + error.title}
				      className={"dialogTitle hugeText"}
				    >
				      {error.icon}
				      <Spacer size={{ x: "12px" }} />
				      {`Error ${error.code} :`}
				      <Spacer size={{ x: "6px" }} />
				      {error.title}
				    </Panel>
				  }
				  onShutdown={error.onCloseCallback}
				  onModalCloseCallback={error.onModalCloseCallback}
				  actions={error.actions}
				>
				  {error.content}
				</Dialog>
		});
	}

	static showWarning(warningData: IMessageInfo)
	{
		//console.log(warningData);
		const warning = { ...defaultWarning, ...warningData };
		ModalManager.addModal({
			content:
				<Dialog
				  className={"warningDialog"}
				  data-name={"warning-dialog-root-" + warning.title}
				  title={
				    <Panel orientation={LayoutOrientations.Horizontal}
				      data-name={"warning-dialog-header-" + warning.title}
				      className={"dialogTitle hugeText"}
				    >
				      {warning.icon}
				      <Spacer size={{ x: "12px" }} />
				      {warning.title}
				    </Panel>
				  }
				  onShutdown={warning.onCloseCallback}
				  onModalCloseCallback={warning.onModalCloseCallback}
				  actions={warning.actions}
				>
				  {warning.content}
				</Dialog>
		});
	}

	static showMessage(messageData: IMessageInfo)
	{
		//console.log(messageData);
		const message = { ...DefaultMessage, ...messageData };
		ModalManager.addModal({
			content:
				<Dialog
				  className={"messageDialog"}
				  data-name={"message-dialog-root-" + message.title}
				  title={
				    <Panel orientation={LayoutOrientations.Horizontal}
				      data-name={"message-dialog-header-" + message.title}
				      className={"dialogTitle hugeText"}
				    >
				      {message.icon}
				      <Spacer size={{ x: "12px" }} />
				      {message.title}
				    </Panel>
				  }
				  onShutdown={message.onCloseCallback}
				  onModalCloseCallback={message.onModalCloseCallback}
				  actions={message.actions}
				>
				  {message.content}
				</Dialog>
		});
	}
}

// errors
export const SpecificErrors = Object.freeze({
	// asset errors
	Error10000_OrganizationToUserExposure: {
		code: 10000,
		title: "Asset Exposure",
		content: "Company Assets cannot be moved to Personal Assets",
	},
	Error10001_InvalidFileType: {
		code: 10001,
		title: "Invalid File Type",
		content: "The file type is not of a supported asset type.",
	},
	// project errors
	Error20000_ScenarioRenameFailed: {
		code: 20000,
		title: "Project Rename Failed",
		content: <div>
			Could not rename project.<br />
			Please remember that projects must have unique names.
		</div>,
	},
	Error20001_ScenarioCreationFailed: {
		code: 20001,
		title: "Project Creation Failed",
		content: <div>
			Could not create project.<br />
			Please remember that projects must have unique names.
		</div>,
	},
	Error20002_ProjectImportNoFileSelected: {
		code: 20002,
		title: "Project Import Failed",
		content: <div>
			No project file was selected for import.
		</div>,
	},
	Error20003_ScenarioLoadFailed: {
		code: 20003,
		title: "Project Load Failed",
		content: <div>
			Could not load project.<br />
			The save file is likely corrupt - try selecting a save manually.<br />
			If the problem persists, please <a className="highlighted" href="https://scopear.zendesk.com/hc/en-us/requests/new" target="_blank" rel="noopener noreferrer help">contact support</a>.
		</div>,
	},
	Error20004_InvalidProjectName: {
		code: 20004,
		title: "Invalid Project Name",
		content: <div>
			Project name cannot contain '/'.
		</div>,
	},
	Error20005_ProjectHasNoSaves: {
		code: 20005,
		title: "Project Has No Saves",
		content: <div>
			This project does not contain any save data and cannot be opened.
		</div>,
	},
	Error20006_CannotDeleteProject: {
		code: 20006,
		title: "Cannot Delete Project",
		content: <div>
			This project cannot be deleted.
		</div>,
	},
});

// warnings
export const SpecificWarnings = Object.freeze({
	
});

// messages
export const SpecificMessages = Object.freeze({
	Message_ProjectPublishSuccess: {
		title: "Publish Successful",
		content:
			<div>
				Scenario was published successfully.
			</div>,
		actions: [
			{
				label: "Confirm",
			},
		]
	},
	Message_AdvancedOperationsInactive: {
		title: "Advanced Operations Inactive",
		content:
			<div>
				Submitting a revision job from simple mode may result in some operations from advanced mode to not be sent.
			</div>,
		actions: [
			{
				label: "Confirm",
			},
		]
	},
	Message_CannotAccessProject: {
		title: "Cannot Access Project",
		content: 
		<div>
					You do not have access to this project.<br/>
					Please contact the owner of the project.
		</div>,
		actions: [
			{
				label: "OK",
			},
		]
	},
});